// Passwords and sessions for the client portal.
//
// - Each client has their own password. Only a salted hash of it is stored
//   (PBKDF2-SHA256), never the password itself.
// - After logging in, the browser gets a cookie signed with SESSION_SECRET:
//   "<client>.<expiry>.<signature>". Nobody can make one without the secret,
//   and it only opens that one client's page.
// - The project content is only sent by the server once that cookie checks
//   out; it isn't in the page's code.

export type Pass = { salt: string; hash: string; iter: number }
export type Approval = { at: string; total: number; version: string }
/** a client: one login, any number of projects (approvals are per project) */
export type Client = { name: string; pass: Pass; projects: Project[]; approvals?: Record<string, Approval>; logo?: string; lang?: "en" | "nl"; no?: number }

export type Phase = { name: string; start: string; end: string; note?: string }
export type Line = { item: string; note?: string; qty: number; unit: string; rate: number }
export type Group = { name: string; lines: Line[] }
export type Project = {
    /** short name in the address: /<client>/<id> */
    id: string
    title: string
    client: string
    reference: string
    date: string
    intro?: string
    planning: { phases: Phase[]; milestones?: { name: string; date: string }[] }
    estimate: { currency: string; vat: number; validUntil: string; groups: Group[]; notes?: string[] }
}

export type KV = {
    get(key: string): Promise<string | null>
    put(key: string, value: string, o?: { expirationTtl?: number }): Promise<void>
    delete(key: string): Promise<void>
    list(o: { prefix: string; cursor?: string }): Promise<{ keys: { name: string }[]; list_complete: boolean; cursor?: string }>
}
export type Env = {
    PORTAL: KV
    SESSION_SECRET: string
    /** the studio's (Tiago's) own password */
    ADMIN_PASSWORD?: string
    RESEND_API_KEY?: string
    NOTIFY_TO?: string
    NOTIFY_FROM?: string
}

export const COOKIE = "nto_c"
const DAYS = 30
const enc = new TextEncoder()

const b64 = (buf: ArrayBuffer | Uint8Array) => {
    const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
    let s = ""
    for (const b of bytes) s += String.fromCharCode(b)
    return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}
const unb64 = (s: string) => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0))

/** constant-time compare of two strings */
function same(a: string, b: string) {
    if (a.length !== b.length) return false
    let d = 0
    for (let i = 0; i < a.length; i++) d |= a.charCodeAt(i) ^ b.charCodeAt(i)
    return d === 0
}

// (Cloudflare's runtime allows at most 100k PBKDF2 rounds)
export const ITER = 100000

export async function hashPassword(password: string, saltB64?: string, iter = ITER): Promise<Pass> {
    const salt = saltB64 ? unb64(saltB64) : crypto.getRandomValues(new Uint8Array(16))
    const key = await crypto.subtle.importKey("raw", enc.encode(password), "PBKDF2", false, ["deriveBits"])
    const bits = await crypto.subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt, iterations: iter }, key, 256)
    return { salt: b64(salt), hash: b64(bits), iter }
}

export async function checkPassword(password: string, pass: Pass | undefined) {
    // (an unknown client takes as long as a wrong password, so nothing can be
    // learned about which client names exist)
    const p = pass || { salt: "AAAAAAAAAAAAAAAAAAAAAA", hash: "x", iter: ITER }
    const got = await hashPassword(password, p.salt, p.iter)
    return !!pass && same(got.hash, p.hash)
}

async function hmac(secret: string, data: string) {
    const key = await crypto.subtle.importKey("raw", enc.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"])
    return b64(await crypto.subtle.sign("HMAC", key, enc.encode(data)))
}

export async function makeSession(secret: string, slug: string, days = DAYS) {
    const exp = Math.floor(Date.now() / 1000) + days * 86400
    const body = `${slug}.${exp}`
    return { value: `${body}.${await hmac(secret, body)}`, maxAge: days * 86400 }
}

/** the client a request's cookie belongs to, or null */
export async function sessionClient(request: Request, secret: string, name = COOKIE) {
    const raw = (request.headers.get("Cookie") || "").split(/;\s*/).find((c) => c.startsWith(name + "="))
    if (!raw || !secret) return null
    const [slug, exp, sig] = raw.slice(name.length + 1).split(".")
    if (!slug || !exp || !sig) return null
    if (Number(exp) < Date.now() / 1000) return null
    if (!same(await hmac(secret, `${slug}.${exp}`), sig)) return null
    return slug
}

export function cookieHeader(request: Request, value: string, maxAge: number, name = COOKIE) {
    const secure = new URL(request.url).protocol === "https:" ? "; Secure" : ""
    // (a client's link may be opened from an e-mail: Lax; the studio's: Strict)
    const site = name === COOKIE ? "Lax" : "Strict"
    return `${name}=${value}; Path=/; HttpOnly; SameSite=${site}; Max-Age=${maxAge}${secure}`
}

// ---- the studio (Tiago) ----
// A separate cookie, signed with its own key derived from SESSION_SECRET, so a
// client's cookie can never pass as the studio's. Strict same-site, 7 days.
export const ADMIN_COOKIE = "nto_s"
const adminKey = (secret: string) => secret + "|studio"
export const makeAdminSession = (secret: string) => makeSession(adminKey(secret), "studio", 7)
export async function isAdmin(request: Request, env: Env) {
    if (!env.SESSION_SECRET || !env.ADMIN_PASSWORD) return false
    return (await sessionClient(request, adminKey(env.SESSION_SECRET), ADMIN_COOKIE)) === "studio"
}
export async function checkAdminPassword(password: string, env: Env) {
    if (!env.ADMIN_PASSWORD) return false
    // (hash both so the comparison takes the same time whatever was typed)
    const [a, b] = await Promise.all([hmac("cmp", password), hmac("cmp", env.ADMIN_PASSWORD)])
    return same(a, b)
}

export const SLUG = /^[a-z0-9][a-z0-9-]{0,40}$/
/** addresses a client can't have */
export const RESERVED = new Set(["admin", "api", "fonts", "assets", "studio", "new"])

export async function loadClient(env: Env, slug: string): Promise<Client | null> {
    if (!SLUG.test(slug)) return null
    // (the language has its own record, so saving a project never undoes it)
    const [raw, lang] = await Promise.all([env.PORTAL.get(`client:${slug}`), env.PORTAL.get(`lang:${slug}`)])
    if (!raw) return null
    try {
        const c = JSON.parse(raw)
        if (lang === "nl" || lang === "en") c.lang = lang
        // (an older record with a single project)
        if (!c.projects && c.project) {
            c.projects = [{ id: "project", ...c.project }]
            if (c.approval) c.approvals = { project: c.approval }
        }
        c.approvals ||= {}
        return c as Client
    } catch {
        return null
    }
}

export const json = (status: number, body: unknown, headers: Record<string, string> = {}) =>
    new Response(JSON.stringify(body), {
        status,
        headers: { "Content-Type": "application/json", "Cache-Control": "no-store", ...headers },
    })

/** only from the portal's own pages (a browser always says where from) */
export function sameOrigin(request: Request) {
    const host = new URL(request.url).host
    const from = request.headers.get("Origin") || request.headers.get("Referer") || ""
    try {
        return new URL(from).host === host
    } catch {
        return false
    }
}

/** the estimate's total including VAT */
export function totals(e: Project["estimate"]) {
    const net = e.groups.reduce((s, g) => s + g.lines.reduce((t, l) => t + l.qty * l.rate, 0), 0)
    const vat = Math.round(net * e.vat) / 100
    return { net, vat, total: net + vat }
}

/** a short fingerprint of the estimate, so an approval belongs to what was seen */
export function version(project: { estimate: unknown }) {
    const s = JSON.stringify(project.estimate)
    let h = 2166136261
    for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619)
    return (h >>> 0).toString(36)
}

/** where a client went after their address changed (old links keep working); null if nowhere */
export async function movedTo(env: Env, slug: string): Promise<string | null> {
    let at = slug
    for (let i = 0; i < 8; i++) {
        const next = await env.PORTAL.get(`moved:${at}`)
        if (!next) break
        at = next
    }
    return at !== slug && (await env.PORTAL.get(`client:${at}`)) ? at : null
}
